import { RateImportSchema } from '../carriers/useRatesImport';

type RateData = Record<string, any>;

export const normalizeGreatAmerican = (
    data: Record<string, string>[],
    state?: string
): Record<string, string>[] => {
    if (!data || data.length === 0) {
        console.warn('No data provided to normalizeGreatAmerican');
        return [];
    }

    return data.map(row => {
        // Common fields regardless of state
        const normalizedRow: Record<string, string> = {
            carrier: row['AIP'] || 'Great American',
            // Check multiple possible formats for crop year
            cropyear:
                row['CROPYEAR'] || row['CROP YEAR'] || row['Crop Year'] || '',
            state: row['State'] || state || '', // Use State from spreadsheet or fallback to parsed state
            countycode: row['COUNTY CODE'] || '',
            county: row['COUNTY'] || '',
            township: row['TOWNSHIP'] || '',
            range: row['RANGE'] || '',
            formcode: '',
            form: row['FORM'] || '',
            crop: row['CROP'] || '',
            // Standard rate fields
            a: row['A'] || '',
            b: row['B'] || '',
            c: row['C'] || '',
            d: row['D'] || '',
            e: row['E'] || '',
            f: row['F'] || '',
            g: row['G'] || '',
            h: row['H'] || '',
            i: row['I'] || '',
            j: row['J'] || '',
            k: row['K'] || '',
            o: row['O'] || '',
            p1: row['P1'] || '',
            p2: row['P2'] || '',
            e2: row['E2'] || '',
        };

        // Add any other columns found in the data
        // This ensures we capture any new columns in future spreadsheets
        Object.keys(row).forEach(key => {
            const lowerKey = key.toLowerCase();
            const value = row[key]; // Get the value
            const valueStr = String(value ?? ''); // Safely convert to string

            // Skip fields that are already handled or empty/null
            if (
                !Object.keys(normalizedRow).includes(lowerKey) &&
                value != null &&
                valueStr.trim() !== '' && // Check trimmed string representation
                lowerKey !== 'aip' &&
                lowerKey !== 'county code' &&
                lowerKey !== 'crop year' &&
                lowerKey !== 'cropyear'
            ) {
                normalizedRow[lowerKey] = valueStr; // Assign the string value
            }
        });

        return normalizedRow;
    });
};

export const normalizeNAU = (data: RateData[], state?: string): RateData[] => {
    if (!data || data.length === 0) {
        console.warn('No data provided to normalizeNAU');
        return [];
    }

    const aggregationMap: Record<string, RateData> = {};

    data.forEach(row => {
        // Find crop year - could be "Crop Year" column or a year directly (like "2025")
        let cropYear = row['Crop Year'] || row['CropYear'] || '';
        if (!cropYear) {
            const yearColumns = Object.keys(row).filter(key =>
                /^20\d\d$/.test(key)
            );
            if (yearColumns.length > 0) {
                cropYear = yearColumns[0];
            }
        }

        // Extract base fields for the unique key and base info
        const carrier = row['AIP'] || 'NAU';
        const rowState = row['State'] || state || '';
        const county = row['County'] || '';
        const township = row['Township'] || '';
        const range = row['Range'] || '';
        const form = row['Form'] || '';
        // County Code and Crop are intentionally ignored

        // Create unique key
        const uniqueKey = `${carrier}|${cropYear}|${rowState}|${county}|${township}|${range}|${form}`;

        // Initialize the entry in the map if it doesn't exist
        if (!aggregationMap[uniqueKey]) {
            aggregationMap[uniqueKey] = {
                carrier: carrier,
                cropyear: String(cropYear), // Ensure string
                state: rowState,
                county: county,
                township: township,
                range: range,
                form: form,
                formcode: '', // Not present in NAU source
                // Initialize all rate categories as empty
                a: '',
                b: '',
                c: '',
                d: '',
                e: '',
                f: '',
                g: '',
                h: '',
                i: '',
                j: '',
                k: '',
                o: '',
                p1: '',
                p2: '',
                e2: '',
            };
        }

        // Map the rate value to the corresponding category field
        const rateValue = row['Rate'];
        const rateClass = row['Class'];

        if (rateValue != null && rateClass != null) {
            const categoryKey = String(rateClass).toLowerCase().trim();
            const rateValueStr = String(rateValue);

            if (
                categoryKey &&
                aggregationMap[uniqueKey].hasOwnProperty(categoryKey)
            ) {
                // Add/Update the rate category for this combination
                if (
                    aggregationMap[uniqueKey][categoryKey] !== '' &&
                    aggregationMap[uniqueKey][categoryKey] !== rateValueStr
                ) {
                    console.warn(
                        `NAU: Overwriting rate for key '${uniqueKey}', category '${categoryKey}'. Old: ${aggregationMap[uniqueKey][categoryKey]}, New: ${rateValueStr}`
                    );
                }
                aggregationMap[uniqueKey][categoryKey] = rateValueStr;
            } else if (categoryKey) {
                console.warn(
                    `NAU: Unknown rate category '${categoryKey}' found for key '${uniqueKey}'`
                );
            }
        }
    });

    // Convert the aggregated map values into the final array
    const normalizedData = Object.values(aggregationMap);
    return normalizedData;
};

export const normalizeProAg = (
    data: RateData[],
    state?: string
): RateData[] => {
    if (!data || data.length === 0) {
        console.warn('No data provided to normalizeProAg');
        return [];
    }

    return data.map(row => {
        // Create the normalized record with all the base fields
        const normalizedRow: Record<string, string> = {
            carrier: row['AIP'] || row['Carrier'] || 'ProAg',
            cropyear: row['Crop Year'] || row['CropYear'] || '',
            state: row['State'] || state || '',
            countycode: row['County Code'] || '',
            county: row['County'] || '',
            township: row['Township'] || '',
            range: row['Range'] || '',
            formcode: row['Form Code'] || '',
            // For ProAg spreadsheets, look for "Policy Form Name" or column "I" for the Form value
            form: row['Policy Form Name'] || row['Form'] || '',
            crop: row['Crop'] || '',
            // Initialize all rate categories as empty
            a: '',
            b: '',
            c: '',
            d: '',
            e: '',
            f: '',
            g: '',
            h: '',
            i: '',
            j: '',
            k: '',
            o: '',
            p1: '',
            p2: '',
            e2: '',
        };

        // ProAg may follow different patterns for rate categories:
        // 1. Direct mapping (columns named A, B, C, etc.)
        // 2. Category + Rate pattern

        // Check for direct category columns first
        // NOTE: For ProAg, we need to be careful not to use "I" as a rate category
        // if it actually represents "Policy Form Name"
        const categories = [
            'A',
            'B',
            'C',
            'D',
            'E',
            'F',
            'G',
            'H',
            'I',
            'J',
            'K',
            'O',
            'P1',
            'P2',
        ];

        categories.forEach(category => {
            if (row[category]) {
                normalizedRow[category.toLowerCase()] = row[category];
            }
        });

        // If there's a Category/Rate pattern (similar to NAU)
        if (row['Rate'] && row['Category']) {
            const category = row['Category'].toLowerCase().trim();
            if (
                category &&
                normalizedRow.hasOwnProperty(category) &&
                !(category === 'i' && row['Policy Form Name'])
            ) {
                normalizedRow[category] = row['Rate'];
            }
        }

        // Handle potential "Rate Class X" columns where X is the category
        Object.keys(row).forEach(key => {
            if (key.startsWith('Rate Class ') || key.startsWith('Rate Type ')) {
                const category = key.split(' ').pop()?.toLowerCase();
                if (
                    category &&
                    normalizedRow.hasOwnProperty(category) &&
                    !(category === 'i' && row['Policy Form Name'])
                ) {
                    normalizedRow[category] = row[key];
                }
            }
        });

        return normalizedRow;
    });
};

// Renamed function for ID/OR/WY/Other RCIS formats (using named headers)
const _normalizeRCIS_Other = (data: RateData[], state?: string): RateData[] => {
    // Based on RCIS-ID-Class-A-P.csv structure, similar to GreatAmerican
    if (!data || data.length === 0) {
        console.warn('No data provided to _normalizeRCIS_Other');
        return [];
    }

    return data.map(row => {
        const getStr = (key: string, defaultValue = '') =>
            row && typeof row === 'object' && row[key] != null
                ? String(row[key]).trim()
                : defaultValue;

        // --- Carrier Logic ---
        let carrierValue = getStr('AIP');
        if (!carrierValue) {
            carrierValue = getStr(''); // Fallback to positional key '' for Column A
        }
        if (!carrierValue) {
            carrierValue = 'RCIS'; // Default if both fail
            // console.log('RCIS Other: Used default carrier 'RCIS''); // Optional log
        }

        // Map columns from the RCIS spreadsheet to the standard schema
        const normalizedRow: Partial<RateImportSchema> = {
            carrier: carrierValue,
            cropyear: getStr('CropYear'),
            state: getStr('State', state), // Use state from row, fallback to passed state
            county: getStr('County'),
            township: getStr('Township'),
            range: getStr('Range'),
            form: getStr('FormDescription'), // Use FormDescription for the 'form' field
            crop: getStr('Crop'), // Assuming a 'Crop' column might exist in other RCIS files
            // Map Rate columns A through P
            a: getStr('A'),
            b: getStr('B'),
            c: getStr('C'),
            d: getStr('D'),
            e: getStr('E'),
            f: getStr('F'),
            g: getStr('G'),
            h: getStr('H'),
            i: getStr('I'),
            j: getStr('J'),
            k: getStr('K'),
            l: getStr('L'), // Added L
            m: getStr('M'), // Added M
            n: getStr('N'), // Added N
            o: getStr('O'),
            p: getStr('P'), // Added P
            // p1: getStr('P1'), // P1/P2 don't seem present in this specific file
            // p2: getStr('P2'),
            // a1: getStr('A1'), // A1/A2 don't seem present
            // a2: getStr('A2'),
            e2: getStr('E2'),
        };

        // Clean up any keys that ended up with null/undefined before returning final RateImportSchema type
        Object.keys(normalizedRow).forEach(key => {
            if (
                normalizedRow[key as keyof RateImportSchema] === null ||
                normalizedRow[key as keyof RateImportSchema] === undefined
            ) {
                delete normalizedRow[key as keyof RateImportSchema];
            }
        });

        return normalizedRow as RateImportSchema; // Cast to final type
    });
};

// NEW Function for RCIS-MT format (using named headers, special handling for CropYear)
const _normalizeRCIS_MT = (data: RateData[], state?: string): RateData[] => {
    if (!data || data.length === 0) {
        console.warn('No data provided to _normalizeRCIS_MT');
        return [];
    }

    return data.map(row => {
        const getStr = (key: string, defaultValue = '') =>
            row && typeof row === 'object' && row[key] != null
                ? String(row[key]).trim()
                : defaultValue;

        // Directly use positional key '_1' (Column B) for MT Crop Year
        let cropYearValue = getStr('_1');
        if (!cropYearValue) {
            console.warn(
                "RCIS MT: Could not determine Crop Year using positional key '_1'."
            );
            cropYearValue = '2025'; // Default if positional key also fails
        }

        // --- Form Logic (Column J) ---
        let formValue = getStr('FormDescription');
        if (!formValue) {
            formValue = getStr('Form'); // Fallback to 'Form' header
        }
        if (!formValue) {
            formValue = getStr('_9'); // Fallback to positional key '_9' for Column J
        }
        if (!formValue) {
            console.warn(
                "RCIS MT: Could not determine Form using 'FormDescription', 'Form', or '_9'."
            );
        }

        const normalizedRow: Partial<RateImportSchema> = {
            carrier: getStr('AIP'),
            cropyear: cropYearValue,
            state: getStr('State', state),
            // countycode: getStr('CountyCode'), // Ignoring
            county: getStr('County'),
            township: getStr('Township'),
            range: getStr('Range'),
            // formcode: getStr('FormCode'), // Ignoring
            form: formValue, // Use determined form value
            crop: getStr('Crop'),
            a: getStr('A'),
            b: getStr('B'),
            c: getStr('C'),
            d: getStr('D'),
            e: getStr('E'),
            f: getStr('F'),
            g: getStr('G'),
            h: getStr('H'),
            i: getStr('I'),
            j: getStr('J'),
            k: getStr('K'),
            l: getStr('L'),
            m: getStr('M'),
            n: getStr('N'),
            o: getStr('O'),
            p: getStr('P'),
        };

        Object.keys(normalizedRow).forEach(key => {
            if (
                normalizedRow[key as keyof RateImportSchema] === null ||
                normalizedRow[key as keyof RateImportSchema] === undefined
            ) {
                delete normalizedRow[key as keyof RateImportSchema];
            }
        });

        return normalizedRow as RateImportSchema;
    });
};

// Main Dispatcher for RCIS
export const normalizeRCIS = (data: RateData[], state?: string): RateData[] => {
    if (!data || data.length === 0) {
        console.warn('No data provided to normalizeRCIS dispatcher');
        return [];
    }

    if (state === 'MT') {
        console.log(`Normalizing RCIS MT format (State: ${state})`);
        return _normalizeRCIS_MT(data, state);
    } else {
        // Assume ID, OR, WY, etc. use the other format
        console.log(`Normalizing RCIS Other format (State: ${state})`);
        return _normalizeRCIS_Other(data, state);
    }
};

export const normalizeAgriSompo = (
    data: RateData[],
    state?: string
): RateData[] => {
    if (!data || data.length === 0) {
        console.warn('No data provided to normalizeAgriSompo');
        return [];
    }

    const _formTypes = [
        'BASIC',
        'Comp 2',
        'Comp 2-10',
        'Comp 3',
        'Comp 4',
        'DDA',
        'DDB',
        'DDC',
        'DD20',
        'DXS5',
        'DXS10',
        'DXS15',
        'XS5',
        'XS10',
        'XS10IP',
        'XS15',
        'XS15IP',
        'XS20',
        'XS20IP',
    ];

    // Check if we're processing the AgriSompo-ID or AgriSompo-WA sheet
    const isIdOrWaSheet = state === 'ID' || state === 'WA';

    // console.log("INCOMING DATA", isIdOrWaSheet); // Removed debug log

    if (isIdOrWaSheet) {
        return normalizeAgriSompoIDWA(data, state);
    } else {
        // Assuming OR/MT or others fall here for now
        return normalizeAgriSompoORMT(data, state);
    }
};

// Function to handle AgriSompo-ID and AgriSompo-WA sheets
const normalizeAgriSompoIDWA = (
    data: RateData[],
    state?: string
): RateData[] => {
    // Hypothesis 3: Papaparse uses Row 1 for keys.
    // data[0] = Row 2 values (Rate Categories: A, B, C...)
    // data[1] = Row 3 values (First actual data row)
    // data[2] = Row 4 values ...

    if (!data || data.length < 2) {
        // Need Row 2 (cats) and at least one data row (Row 3)
        console.warn(
            'Insufficient data for AgriSompo ID/WA normalization (requires at least 2 rows from Papaparse: cats + 1 data row)'
        );
        return [];
    }

    const rateCategoryRow = data[0]; // Row 2 values (A, B, C...) - Keys are from Row 1
    const actualDataRows = data.slice(1); // Data starts from Row 3 (index 1 in Papaparse data)

    // Keys are consistent across all rows after Papaparse parsing with header:true
    const columnKeys = Object.keys(rateCategoryRow);

    // --- Identify Base Column Keys (A-G from Row 1) ---
    const findKey = (
        possibleNames: string[],
        keys: string[]
    ): string | undefined => {
        for (const name of possibleNames) {
            let foundKey = keys.find(
                k => k === name || k.trim().toUpperCase() === name.toUpperCase()
            );
            if (foundKey) return foundKey;
        }
        // Fallback assuming original CSV headers A-G might be keys if descriptive names fail
        if (possibleNames.includes('AIP') && keys.includes('A')) return 'A';
        if (possibleNames.includes('State') && keys.includes('B')) return 'B';
        if (possibleNames.includes('Crop Year') && keys.includes('C'))
            return 'C';
        if (possibleNames.includes('County') && keys.includes('E')) return 'E';
        if (possibleNames.includes('Township') && keys.includes('F'))
            return 'F';
        if (possibleNames.includes('Range') && keys.includes('G')) return 'G';
        return undefined;
    };

    const aipKey = findKey(['AIP', 'A'], columnKeys);
    const stateKey = findKey(['State', 'B'], columnKeys);
    const cropYearKey = findKey(['Crop Year', 'C'], columnKeys);
    const countyKey = findKey(['County', 'E'], columnKeys);
    const townshipKey = findKey(['Township', 'F'], columnKeys);
    const rangeKey = findKey(['Range', 'G'], columnKeys);
    const baseKeys = new Set(
        [
            aipKey,
            stateKey,
            cropYearKey,
            countyKey,
            townshipKey,
            rangeKey,
        ].filter((k): k is string => k != null)
    );

    // Require at least AIP, State, County, Township, Range (5).
    // Crop Year may be absent in some sheets (e.g. 2026 ID).
    const requiredKeys = [aipKey, stateKey, countyKey, townshipKey, rangeKey];
    const missingRequired = requiredKeys.filter(k => k == null);
    if (missingRequired.length > 0) {
        console.error(
            'Could not reliably identify required base column keys (AIP, State, County, Township, Range) using keys from Papaparse',
            { columnKeys, baseKeys, missingRequired }
        );
        return [];
    }

    // --- Identify Rate Column Keys (H onwards from Row 1) ---
    const rateColumnKeys = columnKeys.filter(
        key => !baseKeys.has(key) && key.trim().toUpperCase() !== 'COUNTY CODE'
    );

    // Use a map to aggregate rates by unique combination (location + form)
    const aggregationMap: Record<string, RateData> = {};

    // Process each actual data row (starting from index 1 in Papaparse data)
    actualDataRows.forEach((dataRow, rowIndex) => {
        const csvRowIndex = rowIndex + 3; // For logging purposes: rowIndex 0 = CSV row 3

        // Extract base info for this data row
        const baseInfo = {
            carrier: String(dataRow[aipKey!] ?? 'AgriSompo'),
            state: String(dataRow[stateKey!] ?? state ?? ''),
            cropyear: String(dataRow[cropYearKey!] ?? ''),
            county: String(dataRow[countyKey!] ?? ''),
            township: String(dataRow[townshipKey!] ?? ''),
            range: String(dataRow[rangeKey!] ?? ''),
            formcode: '',
            crop: '',
        };

        // Iterate through the rate columns (keys derived from Row 1)
        rateColumnKeys.forEach(colKey => {
            // Extract base form type by splitting on underscore, taking the first part, AND trimming the result
            const baseFormType = colKey.trim().split('_')[0].trim();
            const rateCategory = String(rateCategoryRow[colKey] ?? '')
                .trim()
                .toLowerCase(); // Rate Category from Row 2 values
            const rateValue = String(dataRow[colKey] ?? '').trim(); // Rate Value from current data row (Row 3+)

            if (baseFormType && rateCategory && rateValue !== '') {
                // Create a unique key for the location + form combination
                const uniqueKey = `${baseInfo.carrier}|${baseInfo.cropyear}|${baseInfo.state}|${baseInfo.county}|${baseInfo.township}|${baseInfo.range}|${baseFormType}`;

                // Initialize the entry in the map if it doesn't exist
                if (!aggregationMap[uniqueKey]) {
                    aggregationMap[uniqueKey] = {
                        ...baseInfo,
                        form: baseFormType,
                        // Initialize all rate fields
                        a: '',
                        b: '',
                        c: '',
                        d: '',
                        e: '',
                        f: '',
                        g: '',
                        h: '',
                        i: '',
                        j: '',
                        k: '',
                        o: '',
                        p1: '',
                        p2: '',
                        e2: '',
                    };
                }

                // Add/Update the rate category for this combination
                if (aggregationMap[uniqueKey].hasOwnProperty(rateCategory)) {
                    aggregationMap[uniqueKey][rateCategory] = rateValue;
                } else {
                    console.warn(
                        `Row ${csvRowIndex}: Unknown rate category '${rateCategory}' encountered for form '${baseFormType}' (Key: ${uniqueKey})`
                    );
                }
            }
        });
    });

    // Convert the aggregated map values into the final array
    const normalizedData = Object.values(aggregationMap);

    return normalizedData;
};

// Function to handle other AgriSompo sheets (e.g., OR, MT)
const normalizeAgriSompoORMT = (
    data: RateData[],
    state?: string
): RateData[] => {
    // OR/MT structure parsed by Papaparse:
    // Row 1 -> Keys: '', '_1', '_2', '_3', '_4', '_5', '_6', FORM_TYPE_H, ...
    // data[0] = Row 2 values (Rate Categories: A, B, C...)
    // data[1] = Row 3 values (First actual data row)

    if (!data || data.length < 2) {
        console.warn(
            'Insufficient data for AgriSompo OR/MT normalization (requires at least 2 rows from Papaparse: cats + 1 data row)'
        );
        return [];
    }

    const rateCategoryRow = data[0]; // Row 2 values (A, B, C...)
    const actualDataRows = data.slice(1); // Data starts from Row 3

    const columnKeys = Object.keys(rateCategoryRow);
    // --- DEBUG LOG ---
    // console.log(`OR/MT Column Keys (State: ${state || 'unknown'}) (from Row 1):`, columnKeys);
    // --- END DEBUG LOG ---

    // --- Define Base Column Keys conditionally based on state ---
    let aipKey: string,
        stateKey: string,
        cropYearKey: string,
        countyKey: string,
        townshipKey: string,
        rangeKey: string;
    let countyCodeKey: string | undefined;
    let requiredBaseKeys: string[];

    // Detect layout from Row 2 data rather than state name.
    // If _3 holds "County" then there's no County Code column (6 base cols).
    // If _3 holds something else (e.g. a number or "County Code"), County Code
    // occupies _3 and County shifts to _4 (7 base cols).
    const valueAt3 = String(rateCategoryRow['_3'] ?? '')
        .trim()
        .toUpperCase();
    const hasCountyCodeColumn =
        valueAt3 !== 'COUNTY' && columnKeys.includes('_6');

    if (!hasCountyCodeColumn) {
        // 6-column layout: AIP, State, Crop Year, County, Township, Range
        aipKey = '';
        stateKey = '_1';
        cropYearKey = '_2';
        countyKey = '_3';
        townshipKey = '_4';
        rangeKey = '_5';
        countyCodeKey = undefined;
        requiredBaseKeys = [
            aipKey,
            stateKey,
            cropYearKey,
            countyKey,
            townshipKey,
            rangeKey,
        ];
    } else {
        // 7-column layout: AIP, State, Crop Year, County Code, County, Township, Range
        aipKey = '';
        stateKey = '_1';
        cropYearKey = '_2';
        countyCodeKey = '_3';
        countyKey = '_4';
        townshipKey = '_5';
        rangeKey = '_6';
        requiredBaseKeys = [
            aipKey,
            stateKey,
            cropYearKey,
            countyKey,
            townshipKey,
            rangeKey,
        ];
    }

    // Validate that the required base keys exist for the detected structure
    const missingKeys = requiredBaseKeys.filter(
        key => !columnKeys.includes(key)
    );

    if (missingKeys.length > 0) {
        console.error(
            `State ${state}: Could not find required base keys (${missingKeys.join(', ')}) in parsed keys`,
            { columnKeys }
        );
        return [];
    }

    const baseKeys = new Set(requiredBaseKeys);
    // Add county code key to the set *if* it was defined and exists, to filter it out
    if (countyCodeKey && columnKeys.includes(countyCodeKey)) {
        baseKeys.add(countyCodeKey);
    }

    // --- Identify Rate Column Keys (Anything not in baseKeys) ---
    const rateColumnKeys = columnKeys.filter(key => !baseKeys.has(key));

    const aggregationMap: Record<string, RateData> = {};

    actualDataRows.forEach((dataRow, rowIndex) => {
        const csvRowIndex = rowIndex + 3;

        // Check if dataRow is valid
        if (!dataRow || typeof dataRow !== 'object') {
            console.warn(
                `Skipping invalid data row at index ${rowIndex} (CSV row ${csvRowIndex})`
            );
            return; // Skip this iteration
        }

        const baseInfo = {
            // Use the hardcoded keys. Provide defaults.
            carrier: String(dataRow[aipKey] ?? 'AgriSompo'),
            state: String(dataRow[stateKey] ?? state ?? ''),
            cropyear: String(dataRow[cropYearKey] ?? ''),
            county: String(dataRow[countyKey] ?? ''),
            township: String(dataRow[townshipKey] ?? ''),
            range: String(dataRow[rangeKey] ?? ''),
            formcode: '',
            crop: '',
        };

        rateColumnKeys.forEach(colKey => {
            // Check if colKey exists in rateCategoryRow and dataRow before accessing
            if (!(colKey in rateCategoryRow) || !(colKey in dataRow)) {
                // This might happen if rows have inconsistent columns, though Papaparse usually handles this.
                // console.warn(`Column key '${colKey}' missing in rateCategoryRow or dataRow at index ${rowIndex}. Skipping.`);
                return;
            }
            const baseFormType = colKey.trim().split('_')[0].trim();
            const rateCategory = String(rateCategoryRow[colKey] ?? '')
                .trim()
                .toLowerCase();
            const rateValue = String(dataRow[colKey] ?? '').trim();

            if (baseFormType && rateCategory && rateValue !== '') {
                const uniqueKey = `${baseInfo.carrier}|${baseInfo.cropyear}|${baseInfo.state}|${baseInfo.county}|${baseInfo.township}|${baseInfo.range}|${baseFormType}`;

                if (!aggregationMap[uniqueKey]) {
                    aggregationMap[uniqueKey] = {
                        ...baseInfo,
                        form: baseFormType,
                        a: '',
                        b: '',
                        c: '',
                        d: '',
                        e: '',
                        f: '',
                        g: '',
                        h: '',
                        i: '',
                        j: '',
                        k: '',
                        o: '',
                        p1: '',
                        p2: '',
                        e2: '',
                    };
                }

                if (aggregationMap[uniqueKey].hasOwnProperty(rateCategory)) {
                    aggregationMap[uniqueKey][rateCategory] = rateValue;
                } else {
                    console.warn(
                        `Row ${csvRowIndex}: Unknown rate category '${rateCategory}' encountered for form '${baseFormType}' (Key: ${uniqueKey})`
                    );
                }
            }
        });
    });

    const normalizedData = Object.values(aggregationMap);
    return normalizedData;
};

// Renamed original function to handle ID/OR RainHail formats (dynamic lookup)
const _normalizeRainHailID = (data: RateData[], state?: string): RateData[] => {
    if (!data || data.length < 2) {
        console.warn(
            'Insufficient data for RainHail ID/OR format normalization'
        );
        return [];
    }

    const formTypeRow = data[0];
    const actualDataRows = data.slice(1);
    const columnKeys = Object.keys(formTypeRow);

    // --- Dynamically Find Base Column Keys by searching formTypeRow VALUES ---
    const findKeyByValue = (valueToFind: string): string | undefined => {
        return columnKeys.find(
            key =>
                String(formTypeRow[key] ?? '')
                    .trim()
                    .toUpperCase() === valueToFind.toUpperCase()
        );
    };
    const findYearKey = (): string | undefined => {
        return columnKeys.find(
            key =>
                /^(CROP)?\s?YEAR$/i.test(
                    String(formTypeRow[key] ?? '').trim()
                ) || /^\d{4}$/.test(String(formTypeRow[key] ?? '').trim())
        );
    };

    // The carrier/AIP column may contain "AIP" as a label or the
    // carrier name itself (e.g. "Rain & Hail"). Fall back to the
    // first column key when no known label matches.
    const determinedAipKey =
        findKeyByValue('AIP') ||
        findKeyByValue('Rain & Hail') ||
        findKeyByValue('Rain and Hail') ||
        columnKeys[0];
    const determinedStateKey = findKeyByValue('State'); // Optional find
    const determinedCountyKey = findKeyByValue('County');
    const determinedTownshipKey = findKeyByValue('Township');
    const determinedRangeKey = findKeyByValue('Range');
    const determinedCropYearKey = findYearKey(); // Optional find

    const baseKeysSet = new Set(
        [
            determinedAipKey,
            determinedStateKey,
            determinedCropYearKey,
            determinedCountyKey,
            determinedTownshipKey,
            determinedRangeKey,
        ].filter((k): k is string => k != null) // keep "" (empty string keys are valid PapaParse keys)
    );

    // --- Validate required keys (AIP, County, Township, Range must be found dynamically) ---
    const requiredFoundKeys = {
        aipKey: determinedAipKey,
        countyKey: determinedCountyKey,
        townshipKey: determinedTownshipKey,
        rangeKey: determinedRangeKey,
        // stateKey: determinedStateKey // State is not strictly required to be found here
    };
    const missingKeys = Object.entries(requiredFoundKeys)
        .filter(([_, value]) => value === undefined || value === null)
        .map(([key]) => key);

    if (missingKeys.length > 0) {
        console.error(
            `RainHail ID/OR: Could not dynamically find keys for required columns: ${missingKeys.join(', ')} by searching row 2 values: ${JSON.stringify(formTypeRow)}`
        );
        return [];
    }
    // Log warnings if optional keys weren't found dynamically
    if (!determinedCropYearKey) {
        console.warn(
            `RainHail ID/OR: Could not dynamically find key for 'Crop Year' column. Defaulting to 2025.`
        );
    }
    if (!determinedStateKey) {
        console.warn(
            `RainHail ID/OR: Could not dynamically find key for 'State' column. Using passed state: '${state}'`
        );
    }

    // --- Identify Rate Column Keys ---
    const rateColumnKeys = columnKeys.filter(key => !baseKeysSet.has(key));
    const aggregationMap: Record<string, RateData> = {};

    actualDataRows.forEach((dataRow, rowIndex) => {
        const _csvRowIndex = rowIndex + 3;
        if (!dataRow || typeof dataRow !== 'object') {
            console.warn(
                `RainHail ID/OR: Skipping invalid data row at index ${rowIndex}`
            );
            return;
        }

        // --- Determine state value ---
        let currentState = state ?? ''; // Default to passed state
        if (determinedStateKey && dataRow[determinedStateKey] != null) {
            currentState = String(dataRow[determinedStateKey]); // Use dynamic value if found
        }

        // --- Determine crop year value ---
        let currentCropYear = '2025'; // Default
        if (determinedCropYearKey && dataRow[determinedCropYearKey] != null) {
            currentCropYear = String(dataRow[determinedCropYearKey]); // Use dynamic value if found
        }

        // --- Extract base info ---
        const baseInfo = {
            carrier: String(dataRow[determinedAipKey!] ?? 'Rain & Hail'),
            state: currentState,
            cropyear: currentCropYear,
            county: String(dataRow[determinedCountyKey!] ?? '').trim(),
            township: String(dataRow[determinedTownshipKey!] ?? ''),
            range: String(dataRow[determinedRangeKey!] ?? ''),
            formcode: '',
            crop: '',
        };

        // --- Process Rate Columns ---
        rateColumnKeys.forEach(colKey => {
            if (!(colKey in formTypeRow) || !(colKey in dataRow)) {
                return;
            }
            const keyBase = colKey.split('_')[0].trim();
            const rateCategory = keyBase.toLowerCase();
            const formType = String(formTypeRow[colKey] ?? '').trim();
            const rateValue = String(dataRow[colKey] ?? '').trim();

            if (formType && rateCategory && rateValue !== '') {
                const uniqueKey = `${baseInfo.carrier}|${baseInfo.cropyear}|${baseInfo.state}|${baseInfo.county}|${baseInfo.township}|${baseInfo.range}|${formType}`;
                if (!aggregationMap[uniqueKey]) {
                    aggregationMap[uniqueKey] = {
                        ...baseInfo,
                        form: formType,
                        a: '',
                        a1: '',
                        a2: '',
                        b: '',
                        c: '',
                        d: '',
                        e: '',
                        f: '',
                        g: '',
                        h: '',
                        i: '',
                        j: '',
                        k: '',
                        l: '',
                        m: '',
                        n: '',
                        o: '',
                        p: '',
                        p1: '',
                        p2: '',
                        e2: '',
                    };
                }
                if (aggregationMap[uniqueKey].hasOwnProperty(rateCategory)) {
                    // Optional: Add overwrite warning if needed later
                    aggregationMap[uniqueKey][rateCategory] = rateValue;
                } else {
                    console.warn(
                        `RainHail ID/OR: Unknown rate category '${rateCategory}' from key '${colKey}' encountered for form '${formType}' (Key: ${uniqueKey})`
                    );
                }
            }
        });
    });

    const normalizedData = Object.values(aggregationMap);
    return normalizedData;
};

// NEW Function to handle RainHail-WA Apples format specifically
const _normalizeRainHailWAApples = (
    data: RateData[],
    state?: string,
    _filename?: string
): RateData[] => {
    if (!data || data.length === 0) {
        console.warn('No data provided to _normalizeRainHailWAApples');
        return [];
    }

    console.log(
        `Normalizing RainHail WA Apples format with ${data.length} rows`
    );

    const aggregationMap: Record<string, RateData> = {};

    data.forEach((row, index) => {
        if (!row || typeof row !== 'object') {
            console.warn(
                `RainHail WA Apples: Skipping invalid row at index ${index}`
            );
            return;
        }

        // Helper function to find column value by multiple possible names
        const findColumnValue = (possibleNames: string[]): string => {
            for (const name of possibleNames) {
                if (row[name] != null && String(row[name]).trim() !== '') {
                    return String(row[name]).trim();
                }
            }
            return '';
        };

        // Extract base info using flexible column matching
        // Handle both standard names and auto-generated names from PapaParse
        const baseInfo = {
            carrier: findColumnValue(['AIP', 'Carrier', '_1']) || 'Rain & Hail',
            cropyear: '2025', // Default for current year
            state: findColumnValue(['State', 'STATE', '_2']) || state || 'WA',
            county: findColumnValue(['County', 'COUNTY', '_3']),
            township: findColumnValue(['Township', 'TOWNSHIP', '_4']),
            range: findColumnValue(['Range', 'RANGE', '_5']),
            formcode: '',
            crop: 'Apples (Red)', // This is specifically for apple rates
        };

        // Skip header rows and placeholder rows with generic text
        // These rows contain literal text like "AIP", "State", "County" instead of actual data
        const isHeaderOrPlaceholderRow =
            baseInfo.carrier === 'AIP' ||
            baseInfo.state === 'State' ||
            baseInfo.county === 'County' ||
            baseInfo.township === 'Township' ||
            baseInfo.range === 'Range' ||
            !baseInfo.county ||
            !baseInfo.township ||
            !baseInfo.range ||
            baseInfo.county.trim() === '' ||
            baseInfo.township.trim() === '' ||
            baseInfo.range.trim() === '';

        if (isHeaderOrPlaceholderRow) {
            if (index < 5) {
                console.log(
                    `RainHail WA Apples: Skipping header/placeholder row ${index}:`,
                    baseInfo
                );
            }
            return;
        }

        // Debug logging for first few rows to verify field mapping
        if (index < 3) {
            console.log(`RainHail WA Apples Debug Row ${index}:`, {
                rawRow: row,
                baseInfo: baseInfo,
            });
        }

        // Get all column keys to find rate columns
        const columnKeys = Object.keys(row);

        // Define rate column patterns and their mappings
        const rateColumnMappings = [
            // Standard format: "I Basic", "I Basic 5% Min", "I XS15"
            { pattern: /^I\s+Basic$/i, cropClass: 'i', form: 'Basic' },
            {
                pattern: /^I\s+Basic\s+5%\s+Min$/i,
                cropClass: 'i',
                form: 'Basic 5% Min',
            },
            { pattern: /^I\s+XS15$/i, cropClass: 'i', form: 'XS15' },
            { pattern: /^J\s+Basic$/i, cropClass: 'j', form: 'Basic' },
            {
                pattern: /^J\s+Basic\s+5%\s+Min$/i,
                cropClass: 'j',
                form: 'Basic 5% Min',
            },
            { pattern: /^J\s+XS15$/i, cropClass: 'j', form: 'XS15' },

            // Auto-generated format: "I", "I_1", "I_2", "J", "J_1", "J_2"
            { pattern: /^I$/i, cropClass: 'i', form: 'Basic' },
            { pattern: /^I_1$/i, cropClass: 'i', form: 'Basic 5% Min' },
            { pattern: /^I_2$/i, cropClass: 'i', form: 'XS15' },
            { pattern: /^J$/i, cropClass: 'j', form: 'Basic' },
            { pattern: /^J_1$/i, cropClass: 'j', form: 'Basic 5% Min' },
            { pattern: /^J_2$/i, cropClass: 'j', form: 'XS15' },

            // PapaParse duplicate header format: "Basic", "Basic_1", "XS15", "Basic_2", "Basic_3", "XS15_1"
            { pattern: /^Basic$/i, cropClass: 'i', form: 'Basic' },
            { pattern: /^Basic_1$/i, cropClass: 'i', form: 'Basic 5% Min' },
            { pattern: /^XS15$/i, cropClass: 'i', form: 'XS15' },
            { pattern: /^Basic_2$/i, cropClass: 'j', form: 'Basic' },
            { pattern: /^Basic_3$/i, cropClass: 'j', form: 'Basic 5% Min' },
            { pattern: /^XS15_1$/i, cropClass: 'j', form: 'XS15' },
        ];

        // Process each rate column
        columnKeys.forEach(colKey => {
            // Skip base columns (both standard and auto-generated names) and empty columns
            if (
                [
                    '',
                    'AIP',
                    'Carrier',
                    'State',
                    'STATE',
                    'County',
                    'COUNTY',
                    'Township',
                    'TOWNSHIP',
                    'Range',
                    'RANGE',
                    '_1',
                    '_2',
                    '_3',
                    '_4',
                    '_5',
                ].includes(colKey)
            ) {
                return;
            }

            const rateValue = String(row[colKey] ?? '').trim();
            if (!rateValue || rateValue === '') {
                return;
            }

            // Find matching rate column mapping
            const mapping = rateColumnMappings.find(m =>
                m.pattern.test(colKey)
            );
            if (!mapping) {
                // Log unrecognized columns for debugging (but don't spam the console)
                if (index < 5) {
                    // Only log for first few rows
                    console.log(
                        `RainHail WA Apples: Unrecognized rate column '${colKey}' with value '${rateValue}'`
                    );
                }
                return;
            }

            const { cropClass, form } = mapping;
            const uniqueKey = `${baseInfo.carrier}|${baseInfo.cropyear}|${baseInfo.state}|${baseInfo.county}|${baseInfo.township}|${baseInfo.range}|${form}`;

            if (!aggregationMap[uniqueKey]) {
                aggregationMap[uniqueKey] = {
                    ...baseInfo,
                    form: form,
                    // Initialize all rate fields
                    a: '',
                    b: '',
                    c: '',
                    d: '',
                    e: '',
                    f: '',
                    g: '',
                    h: '',
                    i: '',
                    j: '',
                    k: '',
                    l: '',
                    m: '',
                    n: '',
                    o: '',
                    p: '',
                    p1: '',
                    p2: '',
                    a1: '',
                    a2: '',
                    e2: '',
                    r1: '',
                    r2: '',
                };
            }

            // Map crop class to rate field
            if (aggregationMap[uniqueKey].hasOwnProperty(cropClass)) {
                aggregationMap[uniqueKey][cropClass] = rateValue;
            } else {
                console.warn(
                    `RainHail WA Apples: Unknown crop class '${cropClass}' encountered for form '${form}' (Key: ${uniqueKey})`
                );
            }
        });
    });

    const normalizedData = Object.values(aggregationMap);

    console.log(
        `RainHail WA Apples: Normalized ${normalizedData.length} unique records`
    );
    return normalizedData;
};

// NEW Function to handle RainHail-WA format (positional base keys)
const _normalizeRainHailWA = (data: RateData[], state?: string): RateData[] => {
    // Structure: Row 1 (Rate Categories) -> keys; Row 2 (Headers/Forms) -> data[0]
    // WA files have variations in base columns:
    // - A-P2 format: Base keys '' to '_5' (AIP, State, Year, County, Township, Range)
    // - D-K format: Base keys '' to '_4' (AIP, State, County, Township, Range) - Year might be missing or elsewhere?
    if (!data || data.length < 2) {
        console.warn('Insufficient data for RainHail WA format normalization');
        return [];
    }

    const formTypeRow = data[0];
    const actualDataRows = data.slice(1);
    const columnKeys = Object.keys(formTypeRow);

    // --- Define and Validate Positional Base Keys Conditionally ---
    let aipKey: string,
        stateKey: string,
        cropYearKey: string | undefined,
        countyKey: string,
        townshipKey: string,
        rangeKey: string;
    let expectedBaseKeys: string[];
    let baseKeysSet: Set<string>;

    // Check if key '_5' exists and its value in row 2 indicates the layout
    const valueAtPos5 = String(formTypeRow['_5'] ?? '')
        .trim()
        .toUpperCase();
    const key5Exists = columnKeys.includes('_5');

    if (key5Exists && valueAtPos5 === 'RANGE') {
        // Assume A-P2 layout (Range is at _5)
        console.log('RainHail WA: Detected A-P2 layout (Range at _5)');
        aipKey = ''; // A
        stateKey = '_1'; // B
        cropYearKey = '_2'; // C
        countyKey = '_3'; // D
        townshipKey = '_4'; // E
        rangeKey = '_5'; // F
        expectedBaseKeys = [
            aipKey,
            stateKey,
            cropYearKey,
            countyKey,
            townshipKey,
            rangeKey,
        ];
    } else {
        // Assume D-K layout (Range is likely at _4, rates start at _5 or named key)
        console.log('RainHail WA: Detected D-K layout (Range likely at _4)');
        aipKey = ''; // A
        stateKey = '_1'; // B
        countyKey = '_2'; // C
        townshipKey = '_3'; // D
        rangeKey = '_4'; // E
        cropYearKey = undefined; // Crop year might be missing in this format - needs confirmation if needed
        expectedBaseKeys = [aipKey, stateKey, countyKey, townshipKey, rangeKey];
    }

    baseKeysSet = new Set(expectedBaseKeys.filter(Boolean)); // Filter out potential undefined cropYearKey

    const missingKeys = expectedBaseKeys.filter(
        key => key !== undefined && !columnKeys.includes(key)
    ); // Check non-undefined keys

    if (missingKeys.length > 0) {
        console.error(
            `RainHail WA: Expected positional base keys (${missingKeys.join(', ')}) not found in parsed keys: ${JSON.stringify(columnKeys)}`
        );
        return [];
    }

    // --- Identify Rate Column Keys ---
    // Exclude all base keys now
    const rateColumnKeys = columnKeys.filter(key => !baseKeysSet.has(key));

    const aggregationMap: Record<string, RateData> = {};

    actualDataRows.forEach((dataRow, rowIndex) => {
        const _csvRowIndex = rowIndex + 3;
        if (!dataRow || typeof dataRow !== 'object') {
            console.warn(
                `RainHail WA: Skipping invalid data row at index ${rowIndex}`
            );
            return;
        }

        // --- Extract base info using corrected conditional positional keys ---
        const baseInfo = {
            carrier: String(dataRow[aipKey!] ?? 'Rain & Hail'),
            state: String(dataRow[stateKey!] ?? state ?? ''),
            cropyear: cropYearKey
                ? String(dataRow[cropYearKey] ?? '2025')
                : '2025', // Use key if defined, else default
            county: String(dataRow[countyKey!] ?? '').trim(),
            township: String(dataRow[townshipKey!] ?? ''),
            range: String(dataRow[rangeKey!] ?? ''),
            formcode: '',
            crop: '',
        };

        // --- Process Rate Columns ---
        rateColumnKeys.forEach(colKey => {
            if (!(colKey in formTypeRow) || !(colKey in dataRow)) {
                return;
            }
            const keyBase = colKey.split('_')[0].trim();
            const rateCategory = keyBase.toLowerCase(); // Handles A, A1, A2, etc.
            const formType = String(formTypeRow[colKey] ?? '').trim();
            const rateValue = String(dataRow[colKey] ?? '').trim();

            if (formType && rateCategory && rateValue !== '') {
                const uniqueKey = `${baseInfo.carrier}|${baseInfo.cropyear}|${baseInfo.state}|${baseInfo.county}|${baseInfo.township}|${baseInfo.range}|${formType}`;
                if (!aggregationMap[uniqueKey]) {
                    aggregationMap[uniqueKey] = {
                        ...baseInfo,
                        form: formType,
                        a: '',
                        a1: '',
                        a2: '',
                        b: '',
                        c: '',
                        d: '',
                        e: '',
                        f: '',
                        g: '',
                        h: '',
                        i: '',
                        j: '',
                        k: '',
                        l: '',
                        m: '',
                        n: '',
                        o: '',
                        p: '',
                        p1: '',
                        p2: '',
                        e2: '',
                    };
                }
                if (aggregationMap[uniqueKey].hasOwnProperty(rateCategory)) {
                    // Optional: Add overwrite warning if needed later
                    aggregationMap[uniqueKey][rateCategory] = rateValue;
                } else {
                    console.warn(
                        `RainHail WA: Unknown rate category '${rateCategory}' from key '${colKey}' encountered for form '${formType}' (Key: ${uniqueKey})`
                    );
                }
            }
        });
    });

    const normalizedData = Object.values(aggregationMap);
    return normalizedData;
};

// Function to handle RainHail-MT format (AgriSompo ID/WA style)
const _normalizeRainHailMT = (data: RateData[], state?: string): RateData[] => {
    // Structure: Row 1 (Forms) -> keys; Row 2 (Rates) -> data[0]
    // Papaparse output seems to be: Keys = "", "_1"-"_5", "Form", "Form_1"...
    // Base columns map to positional keys.
    if (!data || data.length < 2) {
        console.warn(
            'Insufficient data for RainHail MT normalization (requires at least 2 rows from Papaparse: rates + 1 data row)'
        );
        return [];
    }

    const rateCategoryRow = data[0]; // Row 2 values (A, B, C...) - Keys are from Row 1
    const actualDataRows = data.slice(1); // Data starts from Row 3

    const columnKeys = Object.keys(rateCategoryRow); // Keys derived from Row 1

    // --- Define Base Column Keys (Positional) ---
    const aipKey = '';
    const stateKey = '_1';
    const cropYearKey = '_2';
    const countyKey = '_3';
    const townshipKey = '_4';
    const rangeKey = '_5';
    const baseKeysSet = new Set([
        aipKey,
        stateKey,
        cropYearKey,
        countyKey,
        townshipKey,
        rangeKey,
    ]);

    // Validate the expected positional base keys exist
    const requiredPositionalKeys = [
        aipKey,
        stateKey,
        cropYearKey,
        countyKey,
        townshipKey,
        rangeKey,
    ];
    const missingKeys = requiredPositionalKeys.filter(
        key => !columnKeys.includes(key)
    );

    if (missingKeys.length > 0) {
        console.error(
            `RainHail MT: Expected positional base keys (${missingKeys.join(', ')}) not found in parsed keys: ${JSON.stringify(columnKeys)}`
        );
        return [];
    }

    // --- Identify Rate/Form Column Keys (all others not in the base set) ---
    const rateFormColumnKeys = columnKeys.filter(key => !baseKeysSet.has(key));

    // Use a map to aggregate rates by unique combination (location + form)
    const aggregationMap: Record<string, RateData> = {};

    // Process each actual data row (starting from index 1 in Papaparse data)
    actualDataRows.forEach((dataRow, rowIndex) => {
        const csvRowIndex = rowIndex + 3; // For logging purposes

        if (!dataRow || typeof dataRow !== 'object') {
            console.warn(
                `RainHail MT: Skipping invalid data row at index ${rowIndex} (CSV row ${csvRowIndex})`
            );
            return;
        }

        // Extract base info for this data row using positional keys
        const baseInfo = {
            carrier: String(dataRow[aipKey] ?? 'Rain & Hail'),
            state: String(dataRow[stateKey] ?? state ?? ''),
            cropyear: String(dataRow[cropYearKey] ?? '2025'), // Default to 2025 if empty
            county: String(dataRow[countyKey] ?? '').trim(),
            township: String(dataRow[townshipKey] ?? ''),
            range: String(dataRow[rangeKey] ?? ''),
            formcode: '',
            crop: '',
        };

        // Iterate through the rate/form columns (keys like Basic, Basic_1 etc.)
        rateFormColumnKeys.forEach(colKey => {
            // Extract base form type by splitting key on underscore and trimming
            const formType = colKey.split('_')[0].trim();
            // Rate Category (A, B, C...) is the value from the second CSV row (rateCategoryRow)
            const rateCategory = String(rateCategoryRow[colKey] ?? '')
                .trim()
                .toLowerCase();
            // Rate Value is from the current data row
            const rateValue = String(dataRow[colKey] ?? '').trim();

            if (formType && rateCategory && rateValue !== '') {
                // Create a unique key for the location + form combination
                const uniqueKey = `${baseInfo.carrier}|${baseInfo.cropyear}|${baseInfo.state}|${baseInfo.county}|${baseInfo.township}|${baseInfo.range}|${formType}`;

                // Initialize the entry in the map if it doesn't exist
                if (!aggregationMap[uniqueKey]) {
                    aggregationMap[uniqueKey] = {
                        ...baseInfo,
                        form: formType,
                        // Initialize all potential rate fields
                        a: '',
                        b: '',
                        c: '',
                        d: '',
                        e: '',
                        f: '',
                        g: '',
                        h: '',
                        i: '',
                        j: '',
                        k: '',
                        l: '',
                        m: '',
                        n: '',
                        o: '',
                        p: '',
                        p1: '',
                        p2: '',
                        e2: '',
                    };
                }

                // Assign rate value to the correct category, checking if it exists
                if (aggregationMap[uniqueKey].hasOwnProperty(rateCategory)) {
                    if (
                        aggregationMap[uniqueKey][rateCategory] !== '' &&
                        aggregationMap[uniqueKey][rateCategory] !== rateValue
                    ) {
                        // console.warn(`RainHail MT: Overwriting rate for key '${uniqueKey}', category '${rateCategory}'. Old: ${aggregationMap[uniqueKey][rateCategory]}, New: ${rateValue}`);
                    }
                    aggregationMap[uniqueKey][rateCategory] = rateValue;
                } else {
                    console.warn(
                        `RainHail MT: Unknown rate category '${rateCategory}' from Row 2 value under form key '${colKey}' encountered for form type '${formType}' (Unique Key: ${uniqueKey})`
                    );
                }
            }
        });
    });

    // Convert the aggregated map values into the final array
    const normalizedData = Object.values(aggregationMap);
    return normalizedData;
};

// Function to handle simple/direct RainHail format (Idaho apples, etc.)
const _normalizeRainHailSimple = (
    data: RateData[],
    state?: string,
    _filename?: string
): RateData[] => {
    if (!data || data.length === 0) {
        console.warn('No data provided to _normalizeRainHailSimple');
        return [];
    }

    console.log(
        `Normalizing RainHail Simple/Direct format with ${data.length} rows`
    );

    // Helper function to format township/range with leading zeros
    const formatTownshipRange = (value: string): string => {
        if (!value) return '';

        // Extract number and direction (e.g., "1N" -> "1" and "N")
        const match = value.match(/^(\d+)([NSEW])$/i);
        if (match) {
            const [, number, direction] = match;
            // Pad number to 3 digits and combine with direction
            return number.padStart(3, '0') + direction.toUpperCase();
        }

        // Return as-is if it doesn't match the expected pattern
        return value;
    };

    const aggregationMap: Record<string, RateData> = {};

    data.forEach((row, index) => {
        if (!row || typeof row !== 'object') {
            console.warn(
                `RainHail Simple: Skipping invalid row at index ${index}`
            );
            return;
        }

        // Extract base info directly from columns
        const rawTownship = String(
            row['TOWNSHIP'] || row['Township'] || ''
        ).trim();
        const rawRange = String(row['RANGE'] || row['Range'] || '').trim();

        const baseInfo = {
            carrier: String(
                row['AIP'] || row['Carrier'] || 'Rain & Hail'
            ).trim(),
            cropyear: String(
                row['CROP_YEAR'] || row['Crop Year'] || row['Year'] || '2025'
            ).trim(),
            state: String(
                row['STATE_NAME'] || row['State'] || state || ''
            ).trim(),
            county: String(row['COUNTY_NAME'] || row['County'] || '').trim(),
            township: formatTownshipRange(rawTownship),
            range: formatTownshipRange(rawRange),
            formcode: '',
            crop: '', // Will be determined from context or filename
        };

        // Get form and class info
        const cropClass = String(row['CROP_CLASS'] || row['Crop Class'] || '')
            .trim()
            .toLowerCase();
        const formType = String(
            row['POLICY_FORM_NAME'] || row['Form'] || ''
        ).trim();
        const rateValue = String(row['RATE'] || '').trim();

        // Skip rows with missing essential data
        if (
            !baseInfo.county ||
            !baseInfo.township ||
            !baseInfo.range ||
            !cropClass ||
            !formType ||
            !rateValue
        ) {
            if (index < 5) {
                console.log(
                    `RainHail Simple: Skipping incomplete row ${index}:`,
                    {
                        baseInfo,
                        cropClass,
                        formType,
                        rateValue,
                        rawTownship,
                        rawRange,
                    }
                );
            }
            return;
        }

        // Create unique key for aggregation
        const uniqueKey = `${baseInfo.carrier}|${baseInfo.cropyear}|${baseInfo.state}|${baseInfo.county}|${baseInfo.township}|${baseInfo.range}|${formType}`;

        // Initialize record if it doesn't exist
        if (!aggregationMap[uniqueKey]) {
            aggregationMap[uniqueKey] = {
                ...baseInfo,
                form: formType,
                // Initialize all rate fields
                a: '',
                b: '',
                c: '',
                d: '',
                e: '',
                f: '',
                g: '',
                h: '',
                i: '',
                j: '',
                k: '',
                l: '',
                m: '',
                n: '',
                o: '',
                p: '',
                p1: '',
                p2: '',
                a1: '',
                a2: '',
                e2: '',
                r1: '',
                r2: '',
            };
        }

        // Map crop class to rate field
        if (aggregationMap[uniqueKey].hasOwnProperty(cropClass)) {
            aggregationMap[uniqueKey][cropClass] = rateValue;
        } else {
            console.warn(
                `RainHail Simple: Unknown crop class '${cropClass}' encountered for form '${formType}' (Key: ${uniqueKey})`
            );
        }
    });

    const normalizedData = Object.values(aggregationMap);

    console.log(
        `RainHail Simple: Normalized ${normalizedData.length} unique records`
    );
    return normalizedData;
};

// Main dispatcher function for Rain & Hail
export const normalizeRainHail = (
    data: RateData[],
    state?: string,
    filename?: string
): RateData[] => {
    if (!data || data.length === 0) {
        console.warn('No data provided to normalizeRainHail');
        return [];
    }

    // Check if this is the WA Apples format by looking for specific patterns
    if (data.length > 0 && data[0] && typeof data[0] === 'object') {
        const firstRowKeys = Object.keys(data[0]);

        // Check for WA apple rates format - must have the specific column structure
        // WA Apples format has columns like "I Basic", "J Basic", etc. (not simple CROP_CLASS/POLICY_FORM_NAME)
        const hasWAAppleFormat =
            firstRowKeys.includes('AIP') &&
            firstRowKeys.includes('State') &&
            firstRowKeys.includes('County') &&
            firstRowKeys.includes('Township') &&
            firstRowKeys.includes('Range') &&
            (firstRowKeys.some(key => key.startsWith('I ')) ||
                firstRowKeys.some(key => key.startsWith('J '))) &&
            // Ensure it's NOT the simple Idaho format with direct CROP_CLASS/POLICY_FORM_NAME columns
            !firstRowKeys.includes('CROP_CLASS') &&
            !firstRowKeys.includes('POLICY_FORM_NAME') &&
            !firstRowKeys.includes('RATE');

        if (hasWAAppleFormat) {
            console.log(
                `Normalizing RainHail WA Apples format (State: ${state}, File: ${filename || 'N/A'})`
            );
            return _normalizeRainHailWAApples(data, state, filename);
        }

        // Check for simple Idaho/direct format (has CROP_CLASS, POLICY_FORM_NAME, RATE columns)
        // Also matches "Crop Class" / "Form" / "RATE" variant (e.g. Rain & Hail WA endorsement files)
        const hasSimpleIdahoFormat =
            firstRowKeys.includes('AIP') &&
            (firstRowKeys.includes('CROP_CLASS') ||
                firstRowKeys.includes('Crop Class')) &&
            (firstRowKeys.includes('POLICY_FORM_NAME') ||
                firstRowKeys.includes('Form')) &&
            firstRowKeys.includes('RATE');

        if (hasSimpleIdahoFormat) {
            console.log(
                `Normalizing RainHail Simple/Direct format (State: ${state}, File: ${filename || 'N/A'})`
            );
            return _normalizeRainHailSimple(data, state, filename);
        }
    }

    // Fallback to existing state-based dispatching
    if (state === 'MT') {
        console.log(`Normalizing RainHail MT format (State: ${state})`);
        return _normalizeRainHailMT(data, state);
    } else if (state === 'WA') {
        console.log(`Normalizing RainHail WA format (State: ${state})`);
        return _normalizeRainHailWA(data, state);
    } else {
        // Default for ID, OR (non-comprates), or other similar formats (dynamic lookup)
        console.log(
            `Normalizing RainHail ID/OR/Other format (State: ${state}, File: ${filename || 'N/A'})`
        );
        return _normalizeRainHailID(data, state);
    }
};
